import { CloseOutlined, ReloadOutlined, RocketOutlined } from "@ant-design/icons";
import { App, Button } from "antd";
import { useCallback, useEffect, useRef } from "react";
import { useBuildIdCheck } from "@/hooks/useBuildIdCheck";
import { isDev } from "@/utils/environment";

/**
 * Simple version update notification component
 * Only works in non-local environments (test, staging, production)
 */
export const VersionNotification = () => {
  const { notification } = App.useApp();
  const notificationShownRef = useRef(false);
  const initialLoadRef = useRef(true);

  // Only enable version checking in non-development environments
  const isLocalEnvironment = isDev();

  const {
    hasUpdate,
    updateToLatest,
    // currentBuildId, // 暂时未使用
    // latestBuildId, // 暂时未使用
    checkBuildId,
  } = useBuildIdCheck({
    checkInterval: import.meta.env.DEV ? 10 * 1000 : 5 * 60 * 1000, // Dev: 10s, Prod: 5min (reduced from 30min)
    checkOnMount: !isLocalEnvironment, // Only check on mount in non-local environments
    autoCheck: !isLocalEnvironment, // Only auto-check in non-local environments
    checkOnVisibilityChange: !isLocalEnvironment, // Only check on visibility change in non-local environments
  });

  // Check for new version immediately when page loads
  useEffect(() => {
    if (isLocalEnvironment) return;

    // Delay check slightly to ensure application is fully initialized
    const timer = setTimeout(() => {
      checkBuildId().catch(console.error);
    }, 2000);

    return () => clearTimeout(timer);
  }, [isLocalEnvironment, checkBuildId]);

  const showUpdateNotification = useCallback(() => {
    // Prevent duplicate notifications
    if (notificationShownRef.current) return;

    const key = "version-update";
    notificationShownRef.current = true;

    notification.open({
      key,
      message: (
        <div className="flex items-center gap-2 text-base font-semibold text-gray-800">
          <RocketOutlined className="!text-blue-500 text-lg" />
          New Version Available
        </div>
      ),
      description: (
        <div className="!text-gray-500 text-sm leading-relaxed mt-1">
          An application update has been detected. Please update immediately for the best experience
          and latest features.
        </div>
      ),
      placement: "topRight",
      duration: 0,
      className: "version-notification w-96 shadow-xl bg-white/95",
      actions: [
        <Button
          key="update"
          type="primary"
          size="middle"
          icon={<ReloadOutlined />}
          className="rounded-lg mr-[10px] !bg-gradient-to-br !from-blue-500 !to-blue-700 border-none shadow-lg shadow-blue-500/30 font-medium"
          onClick={async () => {
            try {
              // Update local build ID
              await updateToLatest();
              // Refresh page
              window.location.reload();
            } catch (error) {
              console.error("Update failed:", error);
            }
            notification.destroy(key);
            notificationShownRef.current = false;
          }}
        >
          Update Now
        </Button>,
        <Button
          key="later"
          size="middle"
          className="rounded-lg border border-gray-300 text-gray-500 font-medium"
          onClick={() => {
            notification.destroy(key);
            notificationShownRef.current = false;
          }}
        >
          Remind Later
        </Button>,
      ],
      closeIcon: <CloseOutlined className="text-gray-400 text-sm" />,
      onClose: () => {
        notificationShownRef.current = false;
      },
    });
  }, [updateToLatest, notification]);

  // Listen for update status - only in non-local environments
  useEffect(() => {
    if (isLocalEnvironment) {
      return;
    }

    // If updates are detected on initial load, record but don't show notification
    // This prevents users from seeing update notifications right after refreshing the page
    if (initialLoadRef.current && hasUpdate) {
      console.log("✅ Update detected on initial load, but notification suppressed");
      initialLoadRef.current = false;
      return;
    }

    // Show notification only when updates are detected after initial load
    if (!initialLoadRef.current && hasUpdate) {
      showUpdateNotification();
    } else if (hasUpdate) {
      // Set flag to false after initial load to prepare for subsequent checks
      initialLoadRef.current = false;
    } else {
      console.log("✅ Current version is up to date");
    }
  }, [hasUpdate, showUpdateNotification, isLocalEnvironment]);

  return null; // This component renders nothing, only responsible for showing notifications
};

export default VersionNotification;
